/**
 * GameThemeContext.js
 * React Context for managing AI-generated game themes
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';

const GameThemeContext = createContext(null);

/**
 * Hook to access game theme
 */
export function useGameTheme() {
  const context = useContext(GameThemeContext);
  if (!context) {
    throw new Error('useGameTheme must be used within a GameThemeProvider');
  }
  return context;
}

/**
 * GameThemeProvider - Wraps the game UI with custom theme
 * @param {Object} props
 * @param {Object} props.themeSpec - UIThemeSpecification from game.data.ui_theme
 * @param {React.ReactNode} props.children
 */
export function GameThemeProvider({ themeSpec, children }) {
  const [customTheme, setCustomTheme] = useState(null);
  const [isThemeReady, setIsThemeReady] = useState(false);

  // Convert theme specification to MUI theme
  useEffect(() => {
    if (!themeSpec) {
      console.log('[GameThemeProvider] No theme spec provided, using defaults');
      setIsThemeReady(true);
      return;
    }

    console.log('[GameThemeProvider] Applying custom theme:', themeSpec);

    try {
      // Load custom fonts if specified
      if (themeSpec.typography) {
        loadCustomFonts(themeSpec.typography);
      }

      // Create MUI theme from theme specification
      const muiTheme = createThemeFromSpec(themeSpec);
      console.log('[GameThemeProvider] MUI theme created successfully');
      setCustomTheme(muiTheme);
      setIsThemeReady(true);
    } catch (error) {
      console.error('[GameThemeProvider] Failed to apply custom theme:', error);
      setIsThemeReady(true); // Continue with default theme
    }
  }, [themeSpec]);

  // Provide theme context value
  const contextValue = useMemo(() => ({
    themeSpec,
    customTheme,
    isThemeReady,
    colors: themeSpec?.colors,
    typography: themeSpec?.typography,
    spacing: themeSpec?.spacing,
    backgroundImage: themeSpec?.backgroundImage,
    customCSS: themeSpec?.customCSS,
    eventTransitions: themeSpec?.eventTransitions
  }), [themeSpec, customTheme, isThemeReady]);

  // If no custom theme or not ready, use default MUI theme
  if (!customTheme || !isThemeReady) {
    return (
      <GameThemeContext.Provider value={contextValue}>
        {children}
      </GameThemeContext.Provider>
    );
  }

  // Wrap with both custom context and MUI theme provider
  return (
    <GameThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={customTheme}>
        {children}
      </MuiThemeProvider>
    </GameThemeContext.Provider>
  );
}

/**
 * Convert UIThemeSpecification to MUI theme object
 */
function createThemeFromSpec(spec) {
  const colors = spec.colors || {};
  const typography = spec.typography || {};
  const spacing = spec.spacing || {};

  return createTheme({
    palette: {
      mode: 'light', // Could be determined from background color brightness
      primary: {
        main: colors.primary || '#1976d2'
      },
      secondary: {
        main: colors.secondary || '#dc004e'
      },
      background: {
        default: colors.background || '#fafafa',
        paper: colors.surface || '#ffffff'
      },
      text: {
        primary: colors.textPrimary || '#000000',
        secondary: colors.textSecondary || '#666666'
      },
      success: {
        main: colors.success || '#4caf50'
      },
      warning: {
        main: colors.warning || '#ff9800'
      },
      error: {
        main: colors.error || '#f44336'
      }
    },
    typography: {
      fontFamily: typography.primaryFont || 'Roboto, Arial, sans-serif',
      fontSize: parseInt(typography.baseFontSize) || 16,
      fontWeightLight: typography.fontWeights?.light || 300,
      fontWeightRegular: typography.fontWeights?.regular || 400,
      fontWeightMedium: typography.fontWeights?.medium || 500,
      fontWeightBold: typography.fontWeights?.bold || 700,
      h1: { fontFamily: typography.secondaryFont || typography.primaryFont },
      h2: { fontFamily: typography.secondaryFont || typography.primaryFont },
      h3: { fontFamily: typography.secondaryFont || typography.primaryFont },
      h4: { fontFamily: typography.secondaryFont || typography.primaryFont },
      h5: { fontFamily: typography.secondaryFont || typography.primaryFont },
      h6: { fontFamily: typography.secondaryFont || typography.primaryFont }
    },
    shape: {
      borderRadius: parseInt(spacing.borderRadius) || 8
    },
    spacing: 8 * (spacing.spacingScale || 1.0), // MUI default spacing is 8px
    shadows: getShadowsForStyle(spacing.shadowStyle || 'medium')
  });
}

/**
 * Load custom fonts from Google Fonts or system
 */
function loadCustomFonts(typography) {
  const fontsToLoad = new Set();

  if (typography.primaryFont && !isSystemFont(typography.primaryFont)) {
    fontsToLoad.add(typography.primaryFont);
  }
  if (typography.secondaryFont && !isSystemFont(typography.secondaryFont)) {
    fontsToLoad.add(typography.secondaryFont);
  }

  // Load fonts from Google Fonts
  fontsToLoad.forEach(font => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${font.replace(/ /g, '+')}:wght@300;400;500;700&display=swap`;
    document.head.appendChild(link);
  });
}

/**
 * Check if font is a system font
 */
function isSystemFont(fontName) {
  const systemFonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Times', 'Courier New',
    'Courier', 'Verdana', 'Georgia', 'Palatino', 'Garamond',
    'Bookman', 'Comic Sans MS', 'Trebuchet MS', 'Impact', 'sans-serif', 'serif', 'monospace'
  ];
  return systemFonts.some(sf => fontName.toLowerCase().includes(sf.toLowerCase()));
}

/**
 * Get shadow array for shadow style
 */
function getShadowsForStyle(style) {
  const baseShadows = createTheme().shadows;

  switch (style) {
    case 'none':
      return baseShadows.map(() => 'none');
    case 'subtle':
      return baseShadows.map(shadow => shadow.replace(/\d+px/g, match => {
        const val = parseInt(match);
        return `${Math.max(1, Math.floor(val * 0.5))}px`;
      }));
    case 'pronounced':
      return baseShadows.map(shadow => shadow.replace(/\d+px/g, match => {
        const val = parseInt(match);
        return `${Math.floor(val * 1.5)}px`;
      }));
    case 'medium':
    default:
      return baseShadows;
  }
}
